import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { type ApexOptions } from 'apexcharts';
import { ThemePalette } from 'mui.theme';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { convertSeconds } from 'utils/helpers/dateTime';
import { capitalizeFirstLetter } from 'utils/helpers/string.manipulation';

interface TimelineChartProps {
  series: any[];
  annotations: ApexOptions['annotations'];
  isDark: boolean;
  loading?: boolean;
  height?: number | string;
  width?: number | string;
  distributed?: boolean;
  setSelectedAssetLocationAndTimestamps: React.Dispatch<
    React.SetStateAction<{
      assetBase: string;
      location: string;
      start_ts: string;
      end_ts: string;
    } | null>
  >;
  setToggleLegendOpen: React.Dispatch<React.SetStateAction<boolean>>;
  timelineView: 'location' | 'asset';
}

const TimelineChart: React.FC<TimelineChartProps> = ({
  series,
  annotations,
  isDark,
  loading = false,
  height = 650,
  width = '100%',
  distributed = false,
  setSelectedAssetLocationAndTimestamps,
  setToggleLegendOpen,
  timelineView,
}) => {
  const options: ApexOptions = {
    chart: {
      height,
      type: 'rangeBar',
      background: isDark ? ThemePalette.dark.contentBackground : '#f6f7fa',
      zoom: { enabled: true, type: 'x', allowMouseWheelZoom: false },
      events: {
        click: (event, chartContext, opts) => {
          // get asset_base, location, start_ts and end_ts
          if (timelineView === 'location') {
            const series = opts?.config?.series[opts?.seriesIndex];
            const dataPoint = series?.data[opts?.dataPointIndex];
            const assetBase = series?.name;
            const location = dataPoint?.location;
            const startTs = dataPoint?.start_ts;
            const endTs = dataPoint?.end_ts;
            if (!assetBase || !location || !startTs || !endTs) return;
            setSelectedAssetLocationAndTimestamps({
              assetBase,
              location,
              start_ts: startTs,
              end_ts: endTs,
            });
            setToggleLegendOpen(true);
          }
          if (timelineView === 'asset') {
            const series = opts?.config?.series[opts?.seriesIndex];
            const dataPoint = series?.data[opts?.dataPointIndex];
            const assetBase = dataPoint?.asset_base;
            const location = dataPoint?.location_name;
            const startTs = dataPoint?.start_ts;
            const endTs = dataPoint?.end_ts;
            if (!assetBase || !location || !startTs || !endTs) return;
            setSelectedAssetLocationAndTimestamps({
              assetBase,
              location,
              start_ts: startTs,
              end_ts: endTs,
            });
            setToggleLegendOpen(true);
          }
        },
      },
    },
    annotations,
    legend: { show: false },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '95%',
        distributed,
        columnWidth: '90%',
        borderRadius: 1,
      },
    },
    dataLabels: {
      enabled: true,
      formatter(val, opts) {
        const { seriesIndex, dataPointIndex, w } = opts;
        const seriesName = w.config.series[seriesIndex].name;
        const point = w.config.series[seriesIndex].data[dataPointIndex];
        if (seriesName.toLowerCase() === 'location') {
          return point.location_name || 'Location';
        }
        if (point?.type && point?.type !== '-') {
          return capitalizeFirstLetter(point.type);
        }
        return seriesName;
      },
      style: { colors: ['#f3f4f5'] },
    },
    xaxis: {
      type: 'datetime',
      axisTicks: { show: true },
      labels: {
        style: { colors: isDark ? '#fff' : '#000' },
        datetimeUTC: true,
      },
      axisBorder: { show: true, color: isDark ? '#fff' : '#000' },
    },
    yaxis: { labels: { style: { colors: isDark ? '#fff' : '#000' } } },
    grid: {
      show: true,
      borderColor: isDark ? '#ffffff20' : '#00000020',
      position: 'back',
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    stroke: { width: 1 },
    fill: { type: 'solid', opacity: 0.6 },
    tooltip: {
      custom({ series, seriesIndex, dataPointIndex, w }) {
        const seriesName = w.globals.seriesNames[seriesIndex];
        const point = w.config.series[seriesIndex].data[dataPointIndex];
        const timeRange = `${new Date(point.y[0]).toUTCString()} - ${new Date(
          point.y[1]
        ).toUTCString()}`;
        let content = `<div style="padding:10px; background:#1f293780; color:#fff; border-radius:8px; min-width:220px; font-family:Arial; opacity:0.8; z-index: 1000">`;
        content += `<div style="font-size:14px; font-weight:bold; margin-bottom:6px">${
          point?.type && point?.type !== '-' ? point?.type : seriesName
        }</div>`;
        content += `<div style="font-size:12px; margin-bottom:4px">🕒 ${timeRange}</div>`;
        if (point.duration && typeof point.duration === 'number') {
          const dur = convertSeconds(point.duration * 60);
          let fmt = '';
          if (+dur.hours > 0) fmt += `${dur.hours} hours `;
          if (+dur.minutes > 0) fmt += `${dur.minutes} minutes `;
          if (+dur.seconds > 0) fmt += `${dur.seconds} seconds `;
          content += `<div style="font-size:12px">🕒 Duration: <strong>${fmt}</strong></div>`;
        }
        if (point.type && point.type !== '-') {
          content += `<div style="font-size:12px">🛰️ Type: <strong>${point.type}</strong></div>`;
        }
        if (
          (seriesName === 'Bolt' && point?.bolt_count !== undefined) ||
          (point?.bolt_count !== undefined && point?.bolt_count >= 0)
        ) {
          content += `<div style="font-size:12px">🔩 Bolts: <strong>${point?.bolt_count}</strong></div>`;
        }
        if (
          (seriesName === 'Drill' && point?.drill_meter !== undefined) ||
          (point?.drill_meter !== undefined && point?.drill_meter >= 0)
        ) {
          content += `<div style="font-size:12px">🛠️ Drill Meters: <strong>${point?.drill_meter?.toFixed(
            2
          )} meters</strong></div>`;
        }
        if (
          (seriesName === 'Bucket' && point?.bucket_count !== undefined) ||
          (point?.bucket_count !== undefined && point?.bucket_count >= 0)
        ) {
          content += `<div style="font-size:12px">🪣 Buckets: <strong>${point?.bucket_count}</strong></div>`;
        }
        if (seriesName === 'Location' || point?.location_name) {
          content += `<div style="font-size:12px">📍 Location: <strong>${point?.location_name}</strong></div>`;
        }
        if (seriesName === 'Idle' || point?.idle_time >= 0) {
          content += `<div style="font-size:12px">⌛ Idle Time: <strong>${point?.idle_time?.toFixed(
            2
          )} hours</strong></div>`;
        }
        if (seriesName === 'Moving' || point?.moving_time >= 0) {
          content += `<div style="font-size:12px">🚗 Moving Time: <strong>${point?.moving_time?.toFixed(
            2
          )} hours</strong></div>`;
        }
        if (
          seriesName === 'Battery Energy Drained' ||
          (point?.battery_energy_drained !== undefined &&
            point?.battery_energy_drained >= 0)
        ) {
          content += `<hr /><div style="font-size:12px">🔋Min Battery Energy Drained: <strong>${point?.min_battery_energy_drained?.toFixed(
            2
          )} kWh</strong></div>`;
        }
        if (
          seriesName === 'Battery Energy Drained' ||
          (point?.battery_energy_drained !== undefined &&
            point?.battery_energy_drained >= 0)
        ) {
          content += `<div style="font-size:12px">🔋Max Battery Energy Drained: <strong>${point?.max_battery_energy_drained?.toFixed(
            2
          )} kWh</strong></div></hr>`;
        }
        if (
          seriesName === 'Battery Charge Status' ||
          (point?.min_battery_charge_status !== undefined &&
            point?.min_battery_charge_status >= 0)
        ) {
          content += `<hr /><div style="font-size:12px">🔋 Min Battery Charge Status: <strong>${+point?.min_battery_charge_status?.toFixed(
            2
          )}%</strong></div>`;
        }
        if (
          seriesName === 'Battery Charge Status' ||
          (point?.max_battery_charge_status !== undefined &&
            point?.max_battery_charge_status >= 0)
        ) {
          content += `<div style="font-size:12px">🔋 Max Battery Charge Status: <strong>${+point?.max_battery_charge_status?.toFixed(
            2
          )}%</strong></div></hr>`;
        }
        if (
          seriesName === 'Battery Accumulated Energy' ||
          (point?.min_battery_accumulated_energy !== undefined &&
            point?.min_battery_accumulated_energy >= 0)
        ) {
          content += `<hr /><div style="font-size:12px">🔋 Min Battery Accumulated Energy: <strong>${+point?.min_battery_accumulated_energy?.toFixed(
            2
          )} kWh</strong></div>`;
        }
        if (
          seriesName === 'Battery Accumulated Energy' ||
          (point?.max_battery_accumulated_energy !== undefined &&
            point?.max_battery_accumulated_energy >= 0)
        ) {
          content += `<div style="font-size:12px">🔋 Max Battery Accumulated Energy: <strong>${+point?.max_battery_accumulated_energy?.toFixed(
            2
          )} kWh</strong></div></hr>`;
        }
        if (
          point?.total_work_hours !== undefined &&
          point?.total_work_hours >= 0
        ) {
          content += `<div style="font-size:12px">⌛ Total Work Hours: <strong>${point?.total_work_hours?.toFixed(
            2
          )} hours</strong></div>`;
        }
        content += `</div>`;
        return content;
      },
    },
  };

  return (
    <div className="relative" style={{ width, height, borderRadius: '8px' }}>
      {loading && (
        <div className="w-full h-full flex justify-center items-center bg-[#3e3e3e62] absolute z-10">
          <SingleFadeLoader isFullScreen={false} />
        </div>
      )}
      <ReactApexChart
        options={options}
        series={series}
        type="rangeBar"
        height={height}
        width={width}
      />
    </div>
  );
};

export default TimelineChart;
